import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Card, Row, Col, Button, Spinner, Alert, ListGroup, Dropdown } from 'react-bootstrap';
import PageHeader from '../components/PageHeader';
import StatusBadge, { STATUS_LABELS } from '../components/StatusBadge';
import { PartyBadgeList } from '../components/PartyBadge';
import { socialMediaUrl, socialMediaDisplay } from '../utils/socialMedia';
import { adminApi, extractError } from '../api/client';
import { fmtDate } from '../lib/lookups';
import { useStates, useCounties } from '../hooks/useLocations';
import { useToast } from '../contexts/ToastContext';
import { useAuth } from '../contexts/AuthContext';

const NEXT_BY_STATUS = {
  draft: ['in_review', 'archived'],
  in_review: ['draft', 'reviewed', 'archived'],
  reviewed: ['in_review', 'published', 'archived'],
  published: ['archived', 'reviewed'],
  archived: ['draft'],
};

export default function CandidateViewPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const toast = useToast();
  const { user: me } = useAuth();
  const isAdmin = me?.role === 'admin';
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [transitioning, setTransitioning] = useState(false);

  const { findById } = useStates();
  const stateRecord = data?.home_state_id ? findById(data.home_state_id) : null;
  const { counties } = useCounties(stateRecord?.id);
  const county = data?.home_county_id ? counties.find((c) => c.id === data.home_county_id) : null;

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        setLoading(true);
        const res = await adminApi.get(`/admin/candidates/${id}`);
        if (active) setData(res.data);
      } catch (e) {
        if (active) setError(extractError(e));
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, [id]);

  async function transitionTo(target) {
    setTransitioning(true);
    try {
      const res = await adminApi.post(`/admin/candidates/${id}/transition`, { status: target });
      setData(res.data);
      toast.success(`Status → ${STATUS_LABELS[target] || target}`);
    } catch (e) {
      toast.error(extractError(e));
    } finally {
      setTransitioning(false);
    }
  }

  async function deleteCandidate() {
    if (!window.confirm(`Delete ${data.full_name}? This cannot be undone.`)) return;
    try {
      await adminApi.delete(`/admin/candidates/${id}`);
      toast.success('Candidate deleted');
      navigate('/candidates');
    } catch (e) {
      toast.error(extractError(e));
    }
  }

  if (loading) {
    return (
      <>
        <PageHeader title="Candidate" />
        <div className="text-center p-5"><Spinner animation="border" /></div>
      </>
    );
  }

  if (error || !data) {
    return (
      <>
        <PageHeader title="Candidate" />
        <div className="page-content"><Alert variant="danger">{error || 'Not found'}</Alert></div>
      </>
    );
  }

  const nextStatuses = NEXT_BY_STATUS[data.status] || [];

  return (
    <>
      <PageHeader
        title={data.full_name}
        breadcrumb={
          <span><Link to={isAdmin ? '/candidates' : '/my-candidates'} className="text-decoration-none">{isAdmin ? 'Candidates' : 'My Candidates'}</Link> / Profile</span>
        }
        actions={
          <div className="d-flex gap-2">
            {isAdmin && (
              <Dropdown>
                <Dropdown.Toggle variant="outline-secondary" disabled={transitioning} data-testid="candidate-transition-dropdown">
                  <i className="bi bi-arrow-repeat me-1" /> Transition
                </Dropdown.Toggle>
                <Dropdown.Menu>
                  {nextStatuses.length === 0 ? (
                    <Dropdown.Item disabled>No transitions available</Dropdown.Item>
                  ) : nextStatuses.map((s) => (
                    <Dropdown.Item key={s} onClick={() => transitionTo(s)} data-testid={`transition-to-${s}`}>
                      Move to {STATUS_LABELS[s]}
                    </Dropdown.Item>
                  ))}
                </Dropdown.Menu>
              </Dropdown>
            )}
            {isAdmin && (
              <Button variant="outline-danger" onClick={deleteCandidate} data-testid="candidate-profile-delete-button">
                <i className="bi bi-trash me-1" /> Delete
              </Button>
            )}
            <Button variant="primary" onClick={() => navigate(`/candidates/${id}/edit`)} data-testid="candidate-profile-edit-button">
              <i className="bi bi-pencil me-1" /> Edit
            </Button>
          </div>
        }
      />
      <div className="page-content">
        <Card className="mb-3">
          <div className="profile-hero">
            <div className="photo" data-testid="candidate-profile-photo">
              {data.photo_url ? (
                <img src={data.photo_url} alt={data.full_name} onError={(e) => { e.target.style.display = 'none'; }} />
              ) : (
                <i className="bi bi-person" />
              )}
            </div>
            <div>
              <h2 className="d-inline-flex align-items-center gap-2">
                {data.full_name}
                {data.verified && (
                  <span
                    className="text-success"
                    title="Verified profile — core facts confirmed by an editor"
                    data-testid="candidate-verified-badge"
                    aria-label="Verified profile"
                  >
                    <i className="bi bi-patch-check-fill" style={{ fontSize: '1.1rem' }} />
                  </span>
                )}
              </h2>
              <div className="meta-row">
                {stateRecord && (
                  <span><i className="bi bi-geo-alt me-1 text-muted" />{stateRecord.name}</span>
                )}
                {county && (
                  <>
                    {stateRecord && <span>·</span>}
                    <span><i className="bi bi-pin-map me-1 text-muted" />{county.name} County</span>
                  </>
                )}
                {!stateRecord && !county && (
                  <span className="text-muted" style={{ fontSize: '0.85rem' }}>Profile-only candidate. Link to a race from any Election Day's detail page.</span>
                )}
              </div>
              <div className="meta-row mt-2">
                <StatusBadge status={data.status} />
                {(data.parties || []).length > 0 && (
                  <span className="ms-2" data-testid="candidate-view-parties">
                    <PartyBadgeList parties={data.parties} full />
                  </span>
                )}
              </div>
            </div>
            <div className="text-end">
              <div className="text-muted" style={{ fontSize: '0.78rem' }}>Last updated</div>
              <div style={{ fontSize: '0.9rem' }}>{fmtDate(data.updated_at)}</div>
            </div>
          </div>
        </Card>

        <Row className="g-3">
          <Col xs={12} lg={8}>
            <Card className="mb-3">
              <Card.Header>Biography</Card.Header>
              <Card.Body>
                {data.biography ? (
                  <p style={{ whiteSpace: 'pre-wrap', marginBottom: 0 }}>{data.biography}</p>
                ) : (
                  <span className="text-muted">No biography provided.</span>
                )}
              </Card.Body>
            </Card>
            <Card className="mb-3">
              <Card.Header>Key issues / positions</Card.Header>
              {(!data.key_issues || data.key_issues.length === 0) ? (
                <Card.Body><span className="text-muted">No issues recorded.</span></Card.Body>
              ) : (
                <ListGroup variant="flush">
                  {data.key_issues.map((issue, i) => (
                    <ListGroup.Item key={i}>
                      <div style={{ fontWeight: 600 }}>{issue.title}</div>
                      {issue.description && <div className="text-muted" style={{ fontSize: '0.9rem' }}>{issue.description}</div>}
                    </ListGroup.Item>
                  ))}
                </ListGroup>
              )}
            </Card>
            <Card>
              <Card.Header>Endorsements</Card.Header>
              {(!data.endorsements || data.endorsements.length === 0) ? (
                <Card.Body><span className="text-muted">No endorsements.</span></Card.Body>
              ) : (
                <ListGroup variant="flush">
                  {data.endorsements.map((en, i) => (
                    <ListGroup.Item key={i}>
                      <div style={{ fontWeight: 600 }}>{en.organization}</div>
                      {en.description && <div className="text-muted" style={{ fontSize: '0.9rem' }}>{en.description}</div>}
                      {en.url && <a href={en.url} target="_blank" rel="noreferrer" style={{ fontSize: '0.85rem' }}>{en.url}</a>}
                    </ListGroup.Item>
                  ))}
                </ListGroup>
              )}
            </Card>
          </Col>
          <Col xs={12} lg={4}>
            <Card className="mb-3">
              <Card.Header>Contact</Card.Header>
              <ListGroup variant="flush">
                <ListGroup.Item>
                  <div className="text-muted" style={{ fontSize: '0.78rem' }}>Campaign website</div>
                  {data.campaign_website ? <a href={data.campaign_website} target="_blank" rel="noreferrer">{data.campaign_website}</a> : <span className="text-muted">—</span>}
                </ListGroup.Item>
                <ListGroup.Item>
                  <div className="text-muted" style={{ fontSize: '0.78rem' }}>Email</div>
                  {data.email || <span className="text-muted">—</span>}
                </ListGroup.Item>
                <ListGroup.Item>
                  <div className="text-muted" style={{ fontSize: '0.78rem' }}>Phone</div>
                  {data.phone || <span className="text-muted">—</span>}
                </ListGroup.Item>
              </ListGroup>
            </Card>
            <Card className="mb-3">
              <Card.Header>Social media</Card.Header>
              <ListGroup variant="flush">
                {[
                  ['twitter', 'Twitter / X', 'bi-twitter-x'],
                  ['facebook', 'Facebook', 'bi-facebook'],
                  ['instagram', 'Instagram', 'bi-instagram'],
                  ['linkedin', 'LinkedIn', 'bi-linkedin'],
                  ['youtube', 'YouTube', 'bi-youtube'],
                ].map(([key, label, icon]) => {
                  const raw = data.social_media?.[key];
                  const url = raw ? socialMediaUrl(key, raw) : '';
                  const display = raw ? socialMediaDisplay(key, raw) : '';
                  return (
                    <ListGroup.Item key={key}>
                      <div style={{ fontSize: '0.85rem' }}>
                        <i className={`bi ${icon} me-2 text-muted`} /> <span className="text-muted">{label}: </span>
                        {url
                          ? <a href={url} target="_blank" rel="noreferrer" data-testid={`view-social-${key}`}>{display}</a>
                          : <span className="text-muted">—</span>}
                      </div>
                    </ListGroup.Item>
                  );
                })}
              </ListGroup>
            </Card>
            <Card className="mb-3">
              <Card.Header>Sources</Card.Header>
              {(!data.source_links || data.source_links.length === 0) ? (
                <Card.Body><span className="text-muted">No sources.</span></Card.Body>
              ) : (
                <ListGroup variant="flush">
                  {data.source_links.map((s, i) => (
                    <ListGroup.Item key={i}>
                      <div style={{ fontWeight: 600, fontSize: '0.9rem' }}>{s.title}</div>
                      <a href={s.url} target="_blank" rel="noreferrer" style={{ fontSize: '0.85rem', wordBreak: 'break-all' }}>{s.url}</a>
                    </ListGroup.Item>
                  ))}
                </ListGroup>
              )}
            </Card>
            <Card>
              <Card.Header>Internal notes</Card.Header>
              <Card.Body>
                {data.internal_notes ? (
                  <p style={{ whiteSpace: 'pre-wrap', marginBottom: 0, fontSize: '0.9rem' }}>{data.internal_notes}</p>
                ) : (
                  <span className="text-muted">No internal notes.</span>
                )}
                {data.reviewed_at && (
                  <div className="text-muted mt-3" style={{ fontSize: '0.8rem' }}>
                    Reviewed {fmtDate(data.reviewed_at)}
                  </div>
                )}
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </div>
    </>
  );
}
